import type { SupabaseClient } from '@supabase/supabase-js'
import { getSupabase } from './supabase'
import type {
  AcceptanceRow,
  AcceptanceWithVersion,
  AgreementWithVersion,
  Database,
  DeliverableVersionPublicRow,
  EntitlementWithProduct,
  MembershipWithOrg,
  OrganizationRow,
} from './types'

/**
 * Typed query layer over supabase-js. Every function returns plain typed rows
 * and throws a readable Error on failure, so UI code never touches raw
 * PostgREST responses. All reads are shaped by the RLS policies in
 * supabase/migrations/0002_rls.sql (fail closed — a user only ever sees the
 * rows their policies allow).
 */
export function createDb(client: SupabaseClient<Database>) {
  return {
    /** Memberships of a user, with the organization embedded. */
    async listMyMemberships(userId: string): Promise<MembershipWithOrg[]> {
      const { data, error } = await client
        .from('memberships')
        .select('id, role, status, organization_id, created_at, organizations ( id, name, slug, kind )')
        .eq('user_id', userId)
        .order('created_at', { ascending: true })

      if (error) throw new Error(`Failed to load memberships: ${error.message}`)
      return (data ?? []) as MembershipWithOrg[]
    },

    /**
     * Create an organization. The handle_new_organization() trigger
     * (SECURITY DEFINER) immediately grants the caller the owner membership.
     */
    async createOrganization(input: { name: string }): Promise<OrganizationRow> {
      const name = input.name.trim()
      const slug = uniqueSlug(name)

      const { data, error } = await client
        .from('organizations')
        .insert({ name, slug, kind: 'client' })
        .select('id, name, slug, kind, description, created_at, updated_at')
        .single()

      if (error) throw new Error(`Failed to create organization: ${error.message}`)
      return data
    },

    /**
     * Legal documents with their current version only (documents readable by
     * any signed-in user per RLS). Includes the plain-language `summary` and
     * the full `rendered_text` the Review & Accept modal renders.
     */
    async listCurrentAgreements(): Promise<AgreementWithVersion[]> {
      const { data, error } = await client
        .from('documents')
        .select(
          'id, doc_type, title, description, document_versions!inner ( id, version, effective_at, content_hash_sha256, summary, rendered_text, is_current )',
        )
        .eq('document_versions.is_current', true)
        .eq('is_active', true)
        .order('doc_type', { ascending: true })

      if (error) throw new Error(`Failed to load agreements: ${error.message}`)
      return (data ?? []) as AgreementWithVersion[]
    },

    /**
     * The caller's acceptance ledger rows, newest first, with the accepted
     * version embedded ("view accepted version"). RLS: own rows only.
     */
    async listMyAcceptances(userId: string): Promise<AcceptanceWithVersion[]> {
      const { data, error } = await client
        .from('acceptances')
        .select(
          'id, document_version_id, accepted_at, document_versions ( id, document_id, version, rendered_text )',
        )
        .eq('user_id', userId)
        .order('accepted_at', { ascending: false })

      if (error) throw new Error(`Failed to load acceptances: ${error.message}`)
      return (data ?? []) as AcceptanceWithVersion[]
    },

    /**
     * Append one acceptance to the IMMUTABLE ledger (RLS insert-only, owner
     * only; UPDATE/DELETE are rejected by trigger for every role). `accepted_at`
     * is deliberately NOT supplied — the database clock records it.
     */
    async insertAcceptance(input: {
      userId: string
      documentVersionId: string
      ip?: string | null
      userAgent?: string | null
      orderId?: string | null
    }): Promise<AcceptanceRow> {
      const { data, error } = await client
        .from('acceptances')
        .insert({
          user_id: input.userId,
          document_version_id: input.documentVersionId,
          ip: input.ip ?? null,
          user_agent: input.userAgent ?? null,
          order_id: input.orderId ?? null,
        })
        .select(
          'id, user_id, document_version_id, accepted_at, ip, user_agent, order_id, created_at, updated_at',
        )
        .single()

      if (error) throw new Error(`Failed to record acceptance: ${error.message}`)
      return data
    },

    /**
     * Current deliverable versions the caller may see (the row-filtered
     * `deliverable_versions_public` view — no storage_path, ever). Filtered to
     * the given product ids and is_current rows.
     */
    async listCurrentDeliverableVersions(
      productIds: string[],
    ): Promise<DeliverableVersionPublicRow[]> {
      if (productIds.length === 0) return []
      const { data, error } = await client
        .from('deliverable_versions_public')
        .select(
          'id, deliverable_id, deliverable_name, deliverable_description, product_id, organization_id, version, required_document_type, is_current, size_bytes, created_at',
        )
        .in('product_id', productIds)
        .eq('is_current', true)
        .order('created_at', { ascending: true })

      if (error) throw new Error(`Failed to load deliverables: ${error.message}`)
      return (data ?? []) as DeliverableVersionPublicRow[]
    },

    /**
     * Entitlements visible to the caller: rows scoped to the user plus rows
     * scoped to any of the caller's organizations (RLS double-checks this).
     */
    async listMyEntitlements(userId: string, organizationIds: string[]): Promise<EntitlementWithProduct[]> {
      let query = client
        .from('entitlements')
        .select(
          'id, status, expires_at, download_quota, downloads_used, created_at, products ( id, name, kind ), organizations ( id, name )',
        )
        .order('created_at', { ascending: false })

      if (organizationIds.length > 0) {
        query = query.or(`user_id.eq.${userId},organization_id.in.(${organizationIds.join(',')})`)
      } else {
        query = query.eq('user_id', userId)
      }

      const { data, error } = await query
      if (error) throw new Error(`Failed to load entitlements: ${error.message}`)
      return (data ?? []) as EntitlementWithProduct[]
    },
  }
}

export type Db = ReturnType<typeof createDb>

/** Lazy singleton wired to the app's Supabase client. */
export function getDb(): Db {
  return createDb(getSupabase())
}

// ── helpers ─────────────────────────────────────────────────────────────────

/** Slugify to the `^[a-z0-9]+(-[a-z0-9]+)*$` pattern enforced by the DB. */
export function slugify(input: string): string {
  const base = input
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '') // strip diacritics left by decomposition
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48)
  return base.length > 0 ? base : 'org'
}

/** Slug + short random suffix to dodge uniqueness collisions. */
export function uniqueSlug(input: string): string {
  const suffix = crypto.randomUUID().replace(/-/g, '').slice(0, 6)
  return `${slugify(input)}-${suffix}`
}
